import React, { useEffect, useState } from "react";
import { Accordion, Badge, Button, Col, Form, InputGroup, Row, Spinner, Table } from "react-bootstrap";
import IsoDatePicker from "../../../components/IsoDatePicker";
import { formatDateDMY } from "../../../utils/dateFormat";
import {
  deleteBookingMiscVendorPayment,
  editBookingMiscVendorPayment,
  getBookingMiscVendorPayments,
  recordBookingMiscVendorPayment,
} from "../../../services/bookingMiscVendorPaymentApi";
import { DeletedPayments, EditedMark, PaymentRowActions, usePaymentCorrections } from "./PaymentCorrections";

const blankPayment = () => ({
  vendorName: "",
  amount: "",
  paymentDate: new Date().toISOString().slice(0, 10),
  paymentMethod: "UPI",
  referenceNumber: "",
  note: "",
});

export default function MiscVendorPayments({ bookingId, category, label, details = [] }) {
  const [summary, setSummary] = useState(null);
  const [payment, setPayment] = useState(blankPayment);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!bookingId) {
      setSummary(null);
      return;
    }
    setLoading(true);
    setError("");
    getBookingMiscVendorPayments(bookingId, category)
      .then(setSummary)
      .catch((err) => setError(err?.response?.data?.message || `Could not load ${label} payments.`))
      .finally(() => setLoading(false));
  }, [bookingId, category, label]);

  const patch = (field, value) => setPayment((old) => ({ ...old, [field]: value }));
  const record = async () => {
    setSaving(true);
    setError("");
    try {
      setSummary(await recordBookingMiscVendorPayment(bookingId, category, {
        ...payment,
        amount: Number(payment.amount || 0),
      }));
      setPayment(blankPayment());
    } catch (err) {
      setError(err?.response?.data?.message || `Could not record ${label} payment.`);
    } finally {
      setSaving(false);
    }
  };

  const corrections = usePaymentCorrections({
    withVendor: true,
    editPayment: (item, change) => editBookingMiscVendorPayment(bookingId, category, item.id, change),
    deletePayment: (item, { reason }) => deleteBookingMiscVendorPayment(bookingId, category, item.id, reason),
    onChanged: setSummary,
  });

  const payable = Number(summary?.totalPayable || 0);
  const paid = Number(summary?.totalPaid || 0);
  const balance = Number(summary?.balance || 0);

  return (
    <Accordion.Item eventKey={`misc-${category}`}>
      <Accordion.Header>
        <div className="d-flex flex-wrap gap-2 align-items-center">
          <span className="fw-semibold">{label}</span>
          {summary && <>
            <Badge bg="secondary">Payable ₹{payable.toLocaleString()}</Badge>
            <Badge bg="success">Paid ₹{paid.toLocaleString()}</Badge>
            <Badge bg={balance > 0 ? "warning" : "success"} text={balance > 0 ? "dark" : undefined}>
              Balance ₹{balance.toLocaleString()}
            </Badge>
          </>}
        </div>
      </Accordion.Header>
      <Accordion.Body>
        {loading && <div className="small text-muted"><Spinner size="sm" className="me-2" />Loading payments...</div>}
        {error && <div className="small text-danger mb-2">{error}</div>}
        {!bookingId && <div className="small text-muted">Save the booking before recording vendor payments.</div>}
        {summary && <>
          {details.length > 0 && (
            <div className="mb-3">
              <div className="fw-semibold mb-2">Payable Details</div>
              <div className="table-responsive">
                <Table bordered size="sm" className="mb-0 align-middle">
                  <thead>
                    <tr><th style={{ width: 44 }}>#</th>
                      <th>Vendor</th>
                      <th>Payment Details</th>
                      <th>Facility</th>
                      <th>Stay</th>
                      <th className="text-end">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {details.map((item, index) => (
                      <tr key={item.key || `${item.vendorName}-${item.facilityName}-${index}`}><td className="small fw-semibold">{index + 1}</td>
                        <td>
                          <div className="fw-semibold">{item.vendorName || "-"}</div>
                          {item.vendorMeta && <div className="small text-muted">{item.vendorMeta}</div>}
                          {(item.contactName || item.contactNumber) && (
                            <div className="small text-muted">
                              Contact: {[item.contactName, item.contactNumber].filter(Boolean).join(" - ")}
                            </div>
                          )}
                        </td>
                        <td>
                          <div className="small">UPI: {item.upiNumber || "Not provided"}</div>
                          {item.upiScannerUrl ? (
                            <div className="small">
                              <a href={item.upiScannerUrl} target="_blank" rel="noreferrer">View UPI scanner</a>
                            </div>
                          ) : (
                            <div className="small text-muted">UPI scanner not uploaded</div>
                          )}
                          {(item.bankAccountHolder || item.bankAccountNumber || item.bankIfsc) && (
                            <div className="small text-muted">
                              Bank: {[item.bankAccountHolder, item.bankAccountNumber, item.bankIfsc ? `IFSC ${item.bankIfsc}` : ""].filter(Boolean).join(" - ")}
                            </div>
                          )}
                        </td>
                        <td>{item.facilityName || "-"}</td>
                        <td>{item.stayLabel || "-"}</td>
                        <td className="text-end">
                          ₹{Number(item.amount || 0).toLocaleString("en-IN")}
                          {item.pricingType === "PER_PERSON" ? " / person / night" : " / night"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>
          )}
          <div className="fw-semibold mb-2">Record Installment</div>
          <Row className="g-2 mb-3">
            <Col md={3}><Form.Label>Vendor Name *</Form.Label><Form.Control value={payment.vendorName} onChange={(e) => patch("vendorName", e.target.value)} /></Col>
            <Col md={3}><Form.Label>Amount *</Form.Label><InputGroup><InputGroup.Text>₹</InputGroup.Text><Form.Control type="number" min="0.01" max={balance || undefined} step="0.01" value={payment.amount} onChange={(e) => patch("amount", e.target.value)} /></InputGroup></Col>
            <Col md={3}><Form.Label>Payment Date *</Form.Label><IsoDatePicker value={payment.paymentDate} onChange={(value) => patch("paymentDate", value)} /></Col>
            <Col md={3}><Form.Label>Method *</Form.Label><Form.Select value={payment.paymentMethod} onChange={(e) => patch("paymentMethod", e.target.value)}><option value="UPI">UPI</option><option value="BANK_TRANSFER">Bank Transfer</option><option value="CASH">Cash</option><option value="OTHER">Other</option></Form.Select></Col>
            <Col md={3}><Form.Label>Reference / UTR</Form.Label><Form.Control value={payment.referenceNumber} onChange={(e) => patch("referenceNumber", e.target.value)} /></Col>
            <Col md={6}><Form.Label>Payment Note *</Form.Label><Form.Control value={payment.note} onChange={(e) => patch("note", e.target.value)} /></Col>
            <Col md={3} className="d-flex align-items-end"><Button className="w-100" onClick={record} disabled={saving || balance <= 0 || !payment.vendorName.trim() || !Number(payment.amount) || !payment.note.trim()}>{saving ? "Recording..." : "Record Payment"}</Button></Col>
          </Row>
          <div className="table-responsive">
            <Table bordered size="sm" className="mb-0 align-middle">
              <thead><tr><th style={{ width: 44 }}>#</th><th>Date</th><th>Vendor</th><th>Amount</th><th>Method</th><th>Reference</th><th>Note</th><th style={{ width: 84 }}>Actions</th></tr></thead>
              <tbody>
                {(summary.transactions || []).map((item, index) => <tr key={item.id}><td className="small fw-semibold">{index + 1}</td><td>{formatDateDMY(item.paymentDate)}</td><td>{item.vendorName}</td><td>₹{Number(item.amount || 0).toLocaleString()}<EditedMark payment={item} /></td><td>{item.paymentMethod?.replaceAll("_", " ")}</td><td>{item.referenceNumber || "-"}</td><td>{item.note}</td><td><PaymentRowActions payment={item} corrections={corrections} disabled={saving} /></td></tr>)}
                {!summary.transactions?.length && <tr><td colSpan={8} className="text-center text-muted">No payments recorded.</td></tr>}
              </tbody>
            </Table>
          </div>
          <DeletedPayments payments={summary.deletedTransactions} showVendor />
          {corrections.modal}
        </>}
      </Accordion.Body>
    </Accordion.Item>
  );
}
